import { db, getOrCreateSettings } from '../db/db.js';
import type { Item } from '../db/schema.js';
import {
  aggregateItemStatus,
  type ExpiryInfo,
  type ExpiryStatus,
} from './expiry.js';

export interface ItemRow {
  item: Item;
  totalQuantity: number;
  earliestExpiresAt: number | null;
  expiry: ExpiryInfo;
}

export interface LocationGroup {
  location: string;
  rows: ItemRow[];
}

export interface InventoryView {
  groups: LocationGroup[];
  todayPanel: ItemRow[];                   // expired + warning
  counts: { total: number; expired: number; warning: number; fresh: number };
}

export async function loadInventoryView(now: number = Date.now()): Promise<InventoryView> {
  const settings = await getOrCreateSettings();
  const items = await db.items.where('status').equals('active').toArray();

  const rows: ItemRow[] = items.map((item) => {
    const totalQuantity = item.batches.reduce((s, b) => s + b.quantity, 0);
    const earliestExpiresAt = item.batches
      .filter((b) => b.quantity > 0 && b.expiresAt !== null)
      .map((b) => b.expiresAt!)
      .reduce<number | null>((acc, t) => (acc === null || t < acc ? t : acc), null);
    const expiry = aggregateItemStatus(item.batches, settings.expiryWarningDays, now);
    return { item, totalQuantity, earliestExpiresAt, expiry };
  });

  rows.sort((a, b) => {
    const rank = (s: ExpiryStatus) => (s === 'expired' ? 0 : s === 'warning' ? 1 : 2);
    const r = rank(a.expiry.status) - rank(b.expiry.status);
    if (r !== 0) return r;
    if (a.earliestExpiresAt !== null && b.earliestExpiresAt !== null) {
      return a.earliestExpiresAt - b.earliestExpiresAt;
    }
    if (a.earliestExpiresAt !== null) return -1;
    if (b.earliestExpiresAt !== null) return 1;
    return a.item.name.localeCompare(b.item.name, 'zh-Hant');
  });

  const groupMap = new Map<string, ItemRow[]>();
  for (const row of rows) {
    const key = row.item.location;
    const arr = groupMap.get(key) ?? [];
    arr.push(row);
    groupMap.set(key, arr);
  }
  const groups: LocationGroup[] = [...groupMap.entries()].map(([location, list]) => ({
    location,
    rows: list,
  }));

  const todayPanel = rows.filter(
    (r) => r.expiry.status === 'expired' || r.expiry.status === 'warning'
  );

  const counts = {
    total: rows.length,
    expired: rows.filter((r) => r.expiry.status === 'expired').length,
    warning: rows.filter((r) => r.expiry.status === 'warning').length,
    fresh: rows.filter((r) => r.expiry.status === 'fresh').length,
  };

  return { groups, todayPanel, counts };
}
